import { z } from 'zod';
import { productCategorySchema, raritySchema } from './enums.js';

/** Admin dashboard analytics. All monetary figures are base-currency cents. */

export const statsQuerySchema = z.object({
  /** Inclusive ISO date (YYYY-MM-DD) range. Defaults to trailing 30 days. */
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});

export type StatsQuery = z.infer<typeof statsQuerySchema>;

/**
 * A best-selling product. `productId`, `category` and `rarity` are null when
 * the product has since been deleted from the catalog — its sales still count
 * toward revenue, sourced from the order-line snapshots.
 */
export const topSellerSchema = z.object({
  productId: z.string().uuid().nullable(),
  name: z.string(),
  slug: z.string(),
  category: productCategorySchema.nullable(),
  rarity: raritySchema.nullable(),
  unitsSold: z.number().int().min(0),
  revenueCents: z.number().int().min(0),
});

export type TopSeller = z.infer<typeof topSellerSchema>;

export const lowStockItemSchema = z.object({
  productId: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  stock: z.number().int().min(0),
  threshold: z.number().int().min(0),
});

export type LowStockItem = z.infer<typeof lowStockItemSchema>;

export const revenuePointSchema = z.object({
  /** ISO date (YYYY-MM-DD). */
  date: z.string(),
  revenueCents: z.number().int().min(0),
  orderCount: z.number().int().min(0),
});

export type RevenuePoint = z.infer<typeof revenuePointSchema>;

export const dashboardStatsSchema = z.object({
  range: z.object({ from: z.string(), to: z.string() }),
  /** Revenue from PAID + FULFILLED orders in range. */
  revenueCents: z.number().int().min(0),
  paidOrderCount: z.number().int().min(0),
  pendingOrderCount: z.number().int().min(0),
  fulfilledOrderCount: z.number().int().min(0),
  cancelledOrderCount: z.number().int().min(0),
  averageOrderValueCents: z.number().int().min(0),
  revenueSeries: z.array(revenuePointSchema),
  topSellers: z.array(topSellerSchema),
  lowStock: z.array(lowStockItemSchema),
});

export type DashboardStats = z.infer<typeof dashboardStatsSchema>;

/** Products at or below this stock level are flagged low. */
export const LOW_STOCK_THRESHOLD = 5;
